import type { ExpandedLayout } from '../config/constants';
import { logger } from '../utils/core/logger';
import { getSessionStorage } from '../utils/web/storageAvailability';
import { conversationScope } from './conversationScope';
import { roseStorage } from './rose-storage';

export type EvaluationRating =
    | 'Accurate & OK'
    | 'Accurate but Not OK'
    | 'Not Accurate but OK'
    | 'Not Accurate & Not OK';

// Base message interface
interface BaseMessage {
    id: string;
    content: string;
    timestamp: number;
}

// User message - only contains user-related properties
export interface UserMessage extends BaseMessage {
    role: 'user';
    // User messages don't have evaluation or streaming properties
}

// Assistant message - only contains assistant-related properties
export interface AssistantMessage extends BaseMessage {
    role: 'assistant';
    isEvaluated?: boolean;
    evaluationRating?: EvaluationRating | null;
    onEvaluate?: (rating: 'thumbs_up' | 'thumbs_down') => void;
    isStreaming?: boolean;
    isThinking?: boolean;
    isError?: boolean; // For marking system errors that need special styling
    suggestedFollowUps?: string[]; // Follow-up questions to continue conversation
    suggestedAnswers?: string[]; // Pre-suggested answers when bot asks a question
    profilingProgress?: {
        current_question: number;
        total_fields: number;
        answered_fields: number;
        is_complete: boolean;
    } | null;
    intent?: string | null; // Classified visitor intent of the question this answers
}

// Union type for backwards compatibility
export type ChatMessage = UserMessage | AssistantMessage;

// Conversation pair structure - clean Q&A pairs
export interface ConversationPair {
    id: string;
    question: UserMessage;
    answer?: AssistantMessage; // Optional for streaming/pending responses
    timestamp: number;
}

/**
 * Mirrors the backend Redis checkpoint TTL (`chat.session_ttl`, 24h). A restored
 * transcript older than this would outlive the agent's server-side memory.
 */
const CHAT_STATE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const isAssistantMessage = (message: ChatMessage): message is AssistantMessage => {
    return message.role === 'assistant';
};

// Factory functions to create properly typed messages
export const createUserMessage = (
    id: string,
    content: string,
    timestamp: number = Date.now()
): UserMessage => ({
    id,
    content,
    role: 'user',
    timestamp,
});

export const createAssistantMessage = (
    id: string,
    content: string,
    options: {
        timestamp?: number;
        isEvaluated?: boolean;
        evaluationRating?: EvaluationRating | null;
        onEvaluate?: (rating: 'thumbs_up' | 'thumbs_down') => void;
        isStreaming?: boolean;
        isThinking?: boolean;
        isError?: boolean;
        suggestedFollowUps?: string[];
        suggestedAnswers?: string[];
        profilingProgress?: AssistantMessage['profilingProgress'];
        intent?: AssistantMessage['intent'];
    } = {}
): AssistantMessage => ({
    id,
    content,
    role: 'assistant',
    timestamp: options.timestamp || Date.now(),
    ...options,
});

// Factory function to create conversation pairs
export const createConversationPair = (
    question: UserMessage,
    answer?: AssistantMessage
): ConversationPair => ({
    id: question.id, // Use question ID as pair ID
    question,
    answer,
    timestamp: question.timestamp,
});

// Import FORM_ASSISTANT_WELCOME_MARKER at runtime to avoid circular dependency
// Factory function to create form assistant welcome conversation pair
// This creates a synthetic conversation pair with a hidden user message marker
// and a welcome assistant message with follow-up suggestions
export const createFormAssistantWelcomePair = (
    welcomeMessage: string,
    followUps: string[],
    welcomeMarker: string
): ConversationPair => {
    const timestamp = Date.now();
    const id = `form-assistant-welcome-${timestamp}`;

    return {
        id,
        question: {
            id: `${id}-trigger`,
            content: welcomeMarker,
            role: 'user',
            timestamp,
        },
        answer: {
            id,
            content: welcomeMessage,
            role: 'assistant',
            timestamp,
            isStreaming: false,
            suggestedFollowUps: followUps,
        },
        timestamp,
    };
};

export interface ChatState {
    conversationPairs: ConversationPair[];
    isExpanded: boolean;
    isExplicitlyMinimized?: boolean;
    viewLayout?: ExpandedLayout;
    sessionId: string;
    siteName: string;
}

// biome-ignore lint/complexity/noStaticOnlyClass: pre-existing pattern, refactor separately
export class ChatStorageService {
    private static getStorageKey(siteName: string): string {
        return `inboundx_chat_state_${conversationScope(siteName)}`;
    }

    private static getViewLayoutStorageKey(siteName: string): string {
        return `inboundx_view_layout_${siteName}`;
    }

    private static parseStoredChatState(savedState: string): ChatState {
        const parsedState = JSON.parse(savedState);
        return {
            ...parsedState,
            // biome-ignore lint/suspicious/noExplicitAny: JSON.parse returns unknown structure
            conversationPairs: parsedState.conversationPairs.map((pair: any) => ({
                ...pair,
                timestamp: new Date(pair.timestamp).getTime(),
                question: {
                    ...pair.question,
                    timestamp: new Date(pair.question.timestamp).getTime(),
                },
                answer: pair.answer
                    ? {
                          ...pair.answer,
                          timestamp: new Date(pair.answer.timestamp).getTime(),
                      }
                    : undefined,
            })),
        };
    }

    static saveChatState(siteName: string, state: ChatState): void {
        try {
            const storageKey = ChatStorageService.getStorageKey(siteName);
            const serializedState = {
                ...state,
                conversationPairs: state.conversationPairs.map((pair) => ({
                    ...pair,
                    timestamp: new Date(pair.timestamp).toISOString(),
                    question: {
                        ...pair.question,
                        timestamp: new Date(pair.question.timestamp).toISOString(),
                    },
                    answer: pair.answer
                        ? {
                              ...pair.answer,
                              timestamp: new Date(pair.answer.timestamp).toISOString(),
                          }
                        : undefined,
                })),
            };
            // Deliberately not stamped with a save time — see getLastMessageAt.
            getSessionStorage().setItem(storageKey, JSON.stringify(serializedState));
            logger.debug(
                'ChatStorage: Saved chat state for',
                siteName,
                'with',
                state.conversationPairs.length,
                'conversation pairs'
            );
        } catch (error) {
            logger.error('ChatStorage: Error saving chat state:', error);
        }
    }

    /**
     * Epoch ms of the newest message in stored chat state. `null` means "cannot
     * date it", which the caller treats as expired.
     *
     * Deliberately measured from message activity rather than from when the
     * state was last written. Only a turn refreshes the backend Redis
     * checkpoint, whereas saveChatState also runs for UI-only changes — expand,
     * minimize, layout toggle. Stamping a save time would let merely opening
     * the widget at hour 20 push the transcript out to hour 44 while the
     * server-side thread still died at hour 24, which is exactly the
     * outlives-the-agent's-memory case the TTL exists to prevent.
     */
    private static getLastMessageAt(savedState: string): number | null {
        try {
            const parsed = JSON.parse(savedState) as {
                conversationPairs?: { timestamp?: unknown; answer?: { timestamp?: unknown } }[];
            };

            // saveChatState serializes timestamps as ISO strings; tolerate both.
            const toEpoch = (value: unknown): number => {
                if (typeof value === 'number') return value;
                if (typeof value === 'string') return Date.parse(value);
                return Number.NaN;
            };

            const newest = (parsed.conversationPairs ?? [])
                .flatMap((pair) => [toEpoch(pair?.timestamp), toEpoch(pair?.answer?.timestamp)])
                .filter((value) => Number.isFinite(value));

            return newest.length ? Math.max(...newest) : null;
        } catch {
            return null;
        }
    }

    static loadChatState(siteName: string): ChatState | null {
        try {
            // IX-4266: the stored transcript is restored however the page was
            // entered. Arriving with a cross-domain referrer (Google, LinkedIn,
            // an email link) or with none at all used to wipe it, which cut
            // 38.5% of "second conversations" out of a visit the visitor had
            // never actually left. sessionStorage still scopes this to the tab.
            const storageKey = ChatStorageService.getStorageKey(siteName);
            const savedState = getSessionStorage().getItem(storageKey);

            if (!savedState) {
                logger.debug('ChatStorage: No saved state found for', siteName);
                return null;
            }

            // The transcript must not outlive the agent's own memory. The server
            // thread is a Redis checkpoint keyed on the session id with a 24h TTL
            // (ixinfra settings `chat.session_ttl`), while sessionStorage lives as
            // long as the tab — which can be days. Restoring past that point shows
            // a conversation the agent has no recollection of, so the visitor's
            // next "and what about pricing?" resolves against nothing.
            const lastMessageAt = ChatStorageService.getLastMessageAt(savedState);
            if (lastMessageAt === null || Date.now() - lastMessageAt > CHAT_STATE_MAX_AGE_MS) {
                logger.debug(
                    'ChatStorage: Stored chat state older than the backend session TTL, discarding for',
                    siteName
                );
                ChatStorageService.clearChatState(siteName);
                return null;
            }

            const state = ChatStorageService.parseStoredChatState(savedState);

            logger.debug(
                'ChatStorage: Loaded chat state for',
                siteName,
                'with',
                state.conversationPairs.length,
                'conversation pairs'
            );
            return state;
        } catch (error) {
            logger.error('ChatStorage: Error loading chat state:', error, {
                siteName,
            });
            return null;
        }
    }

    static getViewLayout(siteName: string): ExpandedLayout {
        try {
            const viewLayoutKey = ChatStorageService.getViewLayoutStorageKey(siteName);
            const savedViewLayout = getSessionStorage().getItem(viewLayoutKey);

            if (savedViewLayout === 'expanded' || savedViewLayout === 'copilot') {
                return savedViewLayout;
            }

            const storageKey = ChatStorageService.getStorageKey(siteName);
            const savedState = getSessionStorage().getItem(storageKey);
            if (!savedState) {
                return 'expanded';
            }

            const parsedState = ChatStorageService.parseStoredChatState(savedState);
            const legacyViewLayout = parsedState.viewLayout;

            if (legacyViewLayout === 'expanded' || legacyViewLayout === 'copilot') {
                getSessionStorage().setItem(viewLayoutKey, legacyViewLayout);
                return legacyViewLayout;
            }
        } catch (error) {
            logger.error('ChatStorage: Error loading view layout:', error, {
                siteName,
            });
        }

        return 'expanded';
    }

    static clearChatState(siteName: string): void {
        try {
            const storageKey = ChatStorageService.getStorageKey(siteName);
            getSessionStorage().removeItem(storageKey);
            logger.debug('ChatStorage: Cleared chat state for', siteName);
        } catch (error) {
            logger.error('ChatStorage: Error clearing chat state:', error, {
                siteName,
            });
        }
    }

    static hasChatHistory(siteName: string): boolean {
        const state = ChatStorageService.loadChatState(siteName);
        return state !== null && state.conversationPairs.length > 0;
    }

    static shouldStartExpanded(siteName: string): boolean {
        const state = ChatStorageService.loadChatState(siteName);
        return state !== null && (state.isExpanded || state.conversationPairs.length > 0);
    }

    static updateSessionId(siteName: string, newSessionId: string): void {
        const state = ChatStorageService.loadChatState(siteName);
        if (state) {
            state.sessionId = newSessionId;
            ChatStorageService.saveChatState(siteName, state);
            logger.debug('ChatStorage: Updated session ID for', siteName, 'to', newSessionId);
        }
    }

    static addConversationPair(siteName: string, pair: ConversationPair): void {
        const state = ChatStorageService.loadChatState(siteName) || {
            conversationPairs: [],
            isExpanded: true,
            sessionId: '',
            siteName: siteName,
        };

        state.conversationPairs.push(pair);
        ChatStorageService.saveChatState(siteName, state);
    }

    static updateLastAnswer(siteName: string, answer: AssistantMessage): void {
        const state = ChatStorageService.loadChatState(siteName);
        if (state && state.conversationPairs.length > 0) {
            const lastPair = state.conversationPairs[state.conversationPairs.length - 1];
            lastPair.answer = answer;
            ChatStorageService.saveChatState(siteName, state);
        }
    }

    static setExpanded(siteName: string, isExpanded: boolean): void {
        const state = ChatStorageService.loadChatState(siteName);
        if (state) {
            state.isExpanded = isExpanded;
            ChatStorageService.saveChatState(siteName, state);
        }
    }

    static setViewLayout(siteName: string, viewLayout: ExpandedLayout): void {
        try {
            const viewLayoutKey = ChatStorageService.getViewLayoutStorageKey(siteName);
            getSessionStorage().setItem(viewLayoutKey, viewLayout);

            const state = ChatStorageService.loadChatState(siteName);
            if (state) {
                ChatStorageService.saveChatState(siteName, { ...state, viewLayout });
            }
        } catch {
            // Ignore storage errors
        }
    }

    private static getCopilotModeKey(siteName: string): string {
        return `inboundx_copilot_mode_${siteName}`;
    }

    static getCopilotModeOverride(siteName: string): boolean {
        try {
            return (
                getSessionStorage().getItem(ChatStorageService.getCopilotModeKey(siteName)) ===
                'true'
            );
        } catch {
            return false;
        }
    }

    static setCopilotModeOverride(siteName: string, enabled: boolean): void {
        try {
            const key = ChatStorageService.getCopilotModeKey(siteName);
            if (enabled) {
                getSessionStorage().setItem(key, 'true');
            } else {
                getSessionStorage().removeItem(key);
            }
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Consumes the flag a chat link sets before navigating, and returns whether to reopen in copilot.
     * On a phone copilot would cover the page just opened, so the normal chat comes back minimized.
     */
    static consumeCopilotModeOverride(
        overrideDomain: string,
        chatDomain: string,
        isMobile: boolean
    ): boolean {
        if (!ChatStorageService.getCopilotModeOverride(overrideDomain)) return false;
        ChatStorageService.setCopilotModeOverride(overrideDomain, false);
        if (!isMobile) return true;

        const state = ChatStorageService.loadChatState(chatDomain);
        if (state) {
            ChatStorageService.saveChatState(chatDomain, {
                ...state,
                isExpanded: false,
                isExplicitlyMinimized: true,
            });
        }
        return false;
    }

    static isPostConversionComplete(domain: string): boolean {
        return roseStorage.isPostConversionComplete(domain);
    }

    static setPostConversionComplete(domain: string): void {
        roseStorage.setPostConversionComplete(domain);
    }

    static clearPostConversionComplete(domain: string): void {
        roseStorage.clearPostConversionComplete(domain);
    }
}
